import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { CalendarDays, CheckCircle2, CircleSlash, MapPin, PhoneCall, PhoneOutgoing, RefreshCw, Search, User as UserIcon, UserPlus, Users } from 'lucide-react';
import {
  Button, Card, Checkbox, Col, Field, Input, Modal, Row, Select, SectionHead, SECTION_TINT, StatCard, Table, Tag, Textarea, Tooltip, cn, message,
} from '../../ui';
import { describeClient } from '../admin/clientOrigin';
import { RoleBadge } from '../../components/RoleBadge';
import { request } from '../../api/client';
import { useConfirmedMutation } from '../../lib/confirm';
import { useDebounce } from '../../hooks/useDebounce';
import { formatIstDate, formatIstTime } from '../../lib/time';

/**
 * INCOMPLETE SIGNUPS — numbers that verified their OTP in the app but never told us who or where they are.
 *
 * Each is a warm lead: they installed the app and proved the number is theirs. The office calls them,
 * logs what happened, fills the form in on the call, or closes the signup with a reason. "To call" is the
 * badge: open for more than 24 hours (time to finish on their own) and nobody has called yet. A signup
 * nobody completes is wiped after 90 days; one that is closed re-opens if the person signs in again.
 */

type Signup = {
  id: string; mobile: string; status: 'OPEN' | 'COMPLETED' | 'CLOSED'; platform?: string | null; name?: string | null;
  stateName?: string | null; stateCode?: string | null; village?: string | null; pincode?: string | null; districtText?: string | null;
  /** A district can be TYPED (`districtText`) or PICKED (`districtId` → `districtName`). Either answers it. */
  districtId?: string | null; districtName?: string | null;
  progress: { done: string[]; missing: string[]; of: number }; startedAt: string; lastSeenAt?: string | null;
  contactCount: number; lastContactAt?: string | null; lastContactOutcomeLabel?: string | null;
  lastContactByName?: string | null; lastContactByRole?: string | null;
  farmerId?: string | null; farmerName?: string | null; farmerVerification?: string | null; completedVia?: string | null;
  closedReasonLabel?: string | null; closedNote?: string | null; closedByName?: string | null; purged?: boolean; toCall: boolean; fresh?: boolean;
  contacts?: Array<{ id: string; outcomeLabel: string; note?: string | null; createdAt: string; byName?: string | null; byRole?: string | null }>;
};

/**
 * WHERE THEY SIGNED IN FROM — the platform's own logo and its proper name, exactly as the activity
 * log shows it (`pages/admin/clientOrigin.tsx`). It used to be the words "Android app" in grey, the
 * same treatment the owner rejected on the audit dialog: name the client plainly, with its logo.
 */
function platformOrigin(platform?: string | null) {
  if (!platform) return null;
  return describeClient({ source: platform, ipAddress: null, device: { platform, appVersion: null, deviceModel: null, deviceId: null } });
}
function PlatformMark({ platform, className }: { platform?: string | null; className?: string }) {
  const o = platformOrigin(platform);
  if (!o) return null;
  return <span className={cn('inline-flex items-center gap-1.5', className)}>{o.icon}{o.label}</span>;
}

export function FarmerSignupsTab() {
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const status = (sp.get('status') ?? 'OPEN').toUpperCase();
  const toCall = sp.get('toCall') === '1';
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 300);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [open, setOpen] = useState<{ id: string; mode: 'view' | 'complete' | 'close' } | null>(null);

  /*
   * A link from the approvals hub or a notification says WHICH sign-up it meant (`?focus=<id>`), and
   * landing on row one of several hundred is the bug that rule exists for. The record opens once, and
   * the param is then stripped so a refetch cannot re-open a record the reader just closed.
   */
  const focusId = sp.get('focus');
  useEffect(() => {
    if (!focusId) return;
    setOpen({ id: focusId, mode: 'view' });
    setSp((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('focus');
      return next;
    }, { replace: true });
  }, [focusId]); // eslint-disable-line react-hooks/exhaustive-deps

  const setFilter = (s: string, call: boolean) => {
    setPage(1);
    setSp((prev) => {
      const next = new URLSearchParams(prev);
      next.set('status', s);
      if (call) next.set('toCall', '1'); else next.delete('toCall');
      return next;
    }, { replace: true });
  };

  const params = useMemo(() => ({ status, ...(toCall ? { toCall: 1 } : {}), ...(search ? { search } : {}), page, pageSize }), [status, toCall, search, page, pageSize]);
  const { data, isFetching, isError, refetch } = useQuery({
    queryKey: ['farmer-signups', params],
    queryFn: () => request<{ items: Signup[]; total: number }>({ url: '/farmer-signups', params }),
  });
  const { data: sum } = useQuery({
    queryKey: ['farmer-signups-summary'],
    queryFn: () => request<{ open: number; toCall: number; completed: number; closed: number }>({ url: '/farmer-signups/summary' }),
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['farmer-signups'] });
    qc.invalidateQueries({ queryKey: ['farmer-signups-summary'] });
    qc.invalidateQueries({ queryKey: ['farmer-signup'] });
    qc.invalidateQueries({ queryKey: ['badges'] });
  };

  const s = sum ?? { open: 0, toCall: 0, completed: 0, closed: 0 };
  const tile = (label: string, value: number, tone: any, icon: React.ReactNode, active: boolean, go: () => void, hint?: string) => (
    <StatCard label={label} value={value} tone={tone} icon={icon} active={active} hint={hint} onClick={value ? go : undefined} />
  );

  const columns: any[] = [
    {
      /*
       * WHO, in one column. "What they told us" used to be a column of its own, and it holds only what
       * somebody typed into the signup form before abandoning it — so for every login that predates the
       * form it was empty, and on a wide screen that column absorbed the table's whole slack and drew a
       * 450px corridor across every row (owner, 2026-09-29). The number is the identity here; a name and
       * place sit under it on the rows that have them.
       */
      title: 'Mobile', key: 'who', width: 170,
      render: (_: any, r: Signup) => (
        <a href={`tel:${r.mobile}`} className="whitespace-nowrap type-body-strong text-content hover:text-brand-700" onClick={(e) => e.stopPropagation()}>
          {r.mobile}
        </a>
      ),
    },
    {
      /*
       * THE NAME, in a column of its own (owner, 2026-09-29 — the table has the room). It was a second
       * line under the number, where the one fact the office reads out on a call sat in the same cell
       * as the thing they dial. Its width is FIXED: most rows have no name yet, and a column left to
       * take the table's slack draws an empty corridor across every one of them.
       */
      title: 'Name', key: 'name', width: 210,
      render: (_: any, r: Signup) => {
        const who = r.farmerName ?? r.name;
        const place = [r.village, r.districtText ?? r.districtName, r.stateName].filter(Boolean).join(', ');
        if (!who && !place) {
          return r.purged
            ? <span className="type-caption text-subtle">Details removed after 90 days</span>
            : <span className="type-caption italic text-subtle">Not given yet</span>;
        }
        return (
          <div className="min-w-0 leading-tight">
            {who && <div className="truncate type-body-strong text-content">{who}</div>}
            {place && <div className="truncate type-caption text-muted">{place}</div>}
          </div>
        );
      },
    },
    {
      /*
       * "0 / 5" is a measurement, and for most of these rows there is nothing to measure: the column
       * reads the form's autosaved draft, so it only moves once somebody starts typing. Everyone who
       * signed in before the form existed (every backfilled legacy login) has no draft at all, and a
       * fraction there reads as "they answered nothing" when the truth is "they never saw the form".
       * So a started form shows its progress; an unstarted one says so in words.
       */
      title: 'Answered', key: 'progress', width: 130,
      render: (_: any, r: Signup) => {
        if (r.status !== 'OPEN') return <span className="text-subtle">—</span>;
        if (!r.progress.done.length) {
          return (
            <Tooltip label="They have not filled in anything yet. The form opens for them on their next sign-in.">
              <span className="whitespace-nowrap type-caption italic text-muted">Not started</span>
            </Tooltip>
          );
        }
        return (
          <Tooltip label={r.progress.missing.length ? `Still missing: ${r.progress.missing.join(', ')}` : 'All answered'}>
            <span className="whitespace-nowrap">{r.progress.done.length} / {r.progress.of}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Signed in', key: 'started', width: 170,
      render: (_: any, r: Signup) => (
        <span className="block leading-tight">
          <span className="whitespace-nowrap">{formatIstDate(r.startedAt)}</span>
          {r.fresh && (
            <Tooltip label="Signed up in the last 24 hours — they may still be filling the form in.">
              <span className="ml-1.5 align-middle"><Tag tone="sky">New</Tag></span>
            </Tooltip>
          )}
          <span className="block whitespace-nowrap type-caption text-muted">
            {r.lastSeenAt && r.lastSeenAt !== r.startedAt ? `last seen ${formatIstDate(r.lastSeenAt)}` : formatIstTime(r.startedAt)}
          </span>
        </span>
      ),
    },
    {
      /*
       * WHERE THEY SIGNED IN FROM, as a column of its own (owner, 2026-09-29). It sat as a third grey
       * line inside WHO, under the number and the place, so the one cell carried three unrelated facts
       * and the platform — the thing that says which app build to ask about on the call — was the one
       * that read as an afterthought. Beside "Signed in", because that is the event it describes.
       */
      title: 'Came in from', key: 'platform', width: 140,
      render: (_: any, r: Signup) => (r.platform
        ? <PlatformMark platform={r.platform} className="whitespace-nowrap type-body text-content" />
        : <span className="type-caption italic text-subtle">Not recorded</span>),
    },
    {
      /*
       * A logged call is somebody's work: the cell says what happened, WHO made the call and WHEN
       * (owner, 2026-09-29 — the name was there, the date was not, so "3 calls · Asmita Kale" could
       * have been this morning or in July). The count only appears once there is more than one.
       */
      title: 'Calls', key: 'calls', width: 210,
      render: (_: any, r: Signup) => (r.contactCount ? (
        <span className="block leading-tight">
          <span className="block type-body-strong text-content">{r.lastContactOutcomeLabel}</span>
          {/* The caller wears their role, the way a person is named everywhere else in the app. */}
          {r.lastContactByName && (
            <span className="flex flex-wrap items-center gap-1 type-caption text-muted">
              by {r.lastContactByName}
              {r.lastContactByRole && <RoleBadge role={r.lastContactByRole} />}
            </span>
          )}
          {r.lastContactAt && (
            <span className="block whitespace-nowrap type-caption text-subtle">
              {formatIstDate(r.lastContactAt)}, {formatIstTime(r.lastContactAt)}
            </span>
          )}
          {r.contactCount > 1 && <span className="mt-0.5 inline-block rounded bg-surface-2 px-1.5 type-caption text-muted">{r.contactCount} calls</span>}
        </span>
      ) : r.toCall ? <Tag tone="gold">Not called yet</Tag> : <span className="text-subtle">—</span>),
    },
    {
      title: 'Outcome', key: 'outcome', width: 170,
      render: (_: any, r: Signup) => {
        if (r.status === 'COMPLETED') return (
          <span className="block leading-tight">
            <Tag tone="green">{r.completedVia === 'STAFF' ? 'Completed on a call' : r.completedVia === 'LINKED' ? 'Added by staff' : 'Completed by farmer'}</Tag>
            {r.farmerVerification === 'UNVERIFIED' && <span className="mt-0.5 block type-caption text-muted">awaiting sales person</span>}
          </span>
        );
        if (r.status === 'CLOSED') return <Tooltip label={r.closedNote ?? ''}><Tag tone="neutral">{r.closedReasonLabel ?? 'Closed'}</Tag></Tooltip>;
        return <Tag tone="blue">Open</Tag>;
      },
    },
    {
      // A row you can act on: ring them, or open the record to log the call / fill the form in for them.
      title: '', key: 'act', width: 150, align: 'right',
      render: (_: any, r: Signup) => (
        <div className="flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
          <Tooltip label={`Call ${r.mobile}`}>
            <a href={`tel:${r.mobile}`} aria-label={`Call ${r.mobile}`}
              className="inline-flex size-8 items-center justify-center rounded-control border border-line text-content hover:bg-surface-2">
              <PhoneCall size={15} />
            </a>
          </Tooltip>
          {r.status === 'OPEN' && (
            <>
              <Tooltip label="Fill the form in for them">
                <Button size="sm" variant="ghost" icon={<UserPlus size={15} />} aria-label="Fill the form in for them"
                  onClick={() => setOpen({ id: r.id, mode: 'complete' })} />
              </Tooltip>
              {/* Stop chasing, from the row. Opens straight on the reason + note, which the endpoint
                  requires. RED, not the same green as its neighbours (owner, 2026-09-29): calling and
                  filling the form in carry the lead forward, this one gives up on it — the row's one
                  destructive act should not be a click away from "call them" in matching ink. */}
              <Tooltip label="Close this signup">
                <Button size="sm" variant="soft-danger" icon={<CircleSlash size={15} />} aria-label="Close this signup"
                  onClick={() => setOpen({ id: r.id, mode: 'close' })} />
              </Tooltip>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <p className="mb-3 type-body text-muted">
        People who verified their number in the app but never filled in their details. Call them, log what happened, or fill the form in for them.
      </p>
      <Row align="stretch" gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>{tile('To call', s.toCall, s.toCall ? 'warning' : 'neutral', <PhoneOutgoing size={18} />, status === 'OPEN' && toCall,
          () => setFilter('OPEN', true), s.open ? `of the ${s.open} open · nobody has called yet` : 'nobody waiting')}</Col>
        <Col xs={12} md={6}>{tile('Open', s.open, 'sky', <Users size={18} />, status === 'OPEN' && !toCall,
          () => setFilter('OPEN', false), 'still to finish signing up')}</Col>
        <Col xs={12} md={6}>{tile('Completed', s.completed, 'success', <CheckCircle2 size={18} />, status === 'COMPLETED',
          () => setFilter('COMPLETED', false), 'now registered farmers')}</Col>
        <Col xs={12} md={6}>{tile('Closed', s.closed, 'neutral', <CircleSlash size={18} />, status === 'CLOSED',
          () => setFilter('CLOSED', false), 'we stopped chasing')}</Col>
      </Row>
      <Card density="compact">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Input prefix={<Search size={16} />} placeholder="Search mobile or name" aria-label="Search mobile or name" className="w-60"
            value={searchInput} onChange={(e) => { setSearchInput(e.target.value); setPage(1); }} />
          <div className="ml-auto">
            <Tooltip label="Refresh"><Button icon={<RefreshCw size={16} />} loading={isFetching} onClick={() => refetch()} aria-label="Refresh" /></Tooltip>
          </div>
        </div>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data?.items ?? []}
          loading={isFetching && !data}
          onRow={(r: Signup) => ({ onClick: () => setOpen({ id: r.id, mode: 'view' }), style: { cursor: 'pointer' } })}
          locale={{ emptyText: isError ? 'Could not load the list — press Refresh.' : 'No signups here.' }}
          pagination={{
            current: page, pageSize, total: data?.total ?? 0, showSizeChanger: true,
            showTotal: (t) => `${t.toLocaleString('en-IN')} ${t === 1 ? 'signup' : 'signups'}`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          }}
        />
      </Card>
      <SignupModal id={open?.id ?? null} initialMode={open?.mode ?? 'view'} onClose={() => setOpen(null)} onChanged={refresh} />
    </div>
  );
}

function SignupModal({ id, initialMode = 'view', onClose, onChanged }: { id: string | null; initialMode?: 'view' | 'complete' | 'close'; onClose: () => void; onChanged: () => void }) {
  const { data: s } = useQuery({ queryKey: ['farmer-signup', id], queryFn: () => request<Signup>({ url: `/farmer-signups/${id}` }), enabled: !!id });
  const { data: opts } = useQuery({
    queryKey: ['farmer-signup-options'],
    queryFn: () => request<{ outcomes: { value: string; label: string }[]; closeReasons: { value: string; label: string }[] }>({ url: '/farmer-signups/options' }),
    staleTime: 3600_000, enabled: !!id,
  });
  const [mode, setMode] = useState<'view' | 'call' | 'close' | 'complete'>(initialMode);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const [note, setNote] = useState('');
  useEffect(() => { setMode(initialMode); setOutcome(null); setReason(null); setNote(''); }, [id, initialMode]);
  const back = () => { setMode('view'); setNote(''); };
  // Opened straight into the form from the row: there is no record behind it to go back to.

  const logCall = useConfirmedMutation(
    { title: 'Log this call?' },
    {
      mutationFn: () => request({ url: `/farmer-signups/${id}/contacts`, method: 'POST', data: { outcome, note: note.trim() || undefined } }),
      onSuccess: () => { message.success('Call logged'); onChanged(); back(); },
      onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Could not log the call'),
    },
  );
  const close = useConfirmedMutation(
    { title: 'Close this signup?', content: 'It leaves the call list. If they sign in again it opens again.' },
    {
      mutationFn: () => request({ url: `/farmer-signups/${id}/close`, method: 'POST', data: { reason, note: note.trim() } }),
      onSuccess: () => { message.success('Signup closed'); onChanged(); if (initialMode === 'close') onClose(); else back(); },
      onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Could not close'),
    },
  );

  const open = s?.status === 'OPEN';
  const footer = mode === 'call' ? (
    <div className="flex justify-end gap-2"><Button onClick={back}>Back</Button>
      <Button variant="primary" disabled={!outcome} loading={logCall.isPending} onClick={() => logCall.mutate()}>Save call</Button></div>
  ) : mode === 'close' ? (
    <div className="flex justify-end gap-2"><Button onClick={initialMode === 'close' ? onClose : back}>{initialMode === 'close' ? 'Cancel' : 'Back'}</Button>
      <Button variant="danger" disabled={!reason || note.trim().length < 3} loading={close.isPending} onClick={() => close.mutate()}>Close signup</Button></div>
  ) : mode === 'complete' ? null : (
    <div className="flex flex-wrap justify-end gap-2">
      {open && (
        <Tooltip label="Stop chasing this sign-up. If they sign in again it opens by itself.">
          <Button variant="soft-danger" icon={<CircleSlash size={15} />} onClick={() => setMode('close')}>Close sign-up</Button>
        </Tooltip>
      )}
      {/* "Log a call" on a record that already lists one reads as "no call has been logged" — the
          record said CALLS 1 · Spoke to them and offered to log the first (owner, 2026-09-29). The
          button names the act it is about to perform, so on a chased lead it is the SECOND call. */}
      {open && (
        <Tooltip label="Record that somebody rang them, and what came of it. The sign-up stays open.">
          <Button icon={<PhoneCall size={15} />} onClick={() => setMode('call')}>
            {(s.contacts ?? []).length ? 'Log another call' : 'Log a call'}
          </Button>
        </Tooltip>
      )}
      {open && (
        <Tooltip label="Enter their details on the call and register them as a farmer.">
          <Button variant="primary" icon={<UserPlus size={15} />} onClick={() => setMode('complete')}>Fill in for them</Button>
        </Tooltip>
      )}
      {!open && <Button onClick={onClose}>Close</Button>}
    </div>
  );

  return (
    <Modal open={!!id} onOpenChange={(o) => { if (!o) onClose(); }} width={480} footer={footer}
      /*
       * The NUMBER is the identity of a signup that has no name yet, so it is the title — and then it
       * must not be repeated in the line underneath (it was, on every record). A named one leads with
       * the name and keeps the number as a fact in the list below, where every other fact is labelled
       * rather than piled up unlabelled.
       */
      title={s ? (s.farmerName ?? s.name ?? s.mobile) : 'Signup'}
      description={s ? (
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="inline-flex items-center gap-1.5 text-muted"><CalendarDays className="size-3.5 text-brand-600 dark:text-brand-400" />Signed in {formatIstDate(s.startedAt)}</span>
          {s.platform && <PlatformMark platform={s.platform} className="rounded-control bg-surface-2 px-2 py-0.5 type-caption text-content" />}
        </span>
      ) : undefined}>
      {!s ? null : mode === 'complete' ? (
        <CompleteForSignup signup={s} onCancel={initialMode === 'complete' ? onClose : back} onDone={() => { onChanged(); onClose(); }} />
      ) : (
        <div className="flex flex-col gap-4">
          {mode === 'view' && (
            <>
              {/* WHAT THEY TOLD US — the same three-level hierarchy every detail view uses (section
                  head with a tinted icon chip, sentence-case label, strong value), and the facts that
                  get read off a screen and dialled or copied wear their own colour: the number is a
                  tap-to-call chip, the PIN code a mono chip, the place a pin chip. An unanswered
                  field is not a grey dash — it is the reason this record exists, so it says so in
                  amber, the tone this app uses for "still pending". */}
              <section className="rounded-card border border-line bg-surface p-3.5">
                <SectionHead icon={<UserIcon className="size-3.5" />} tint={SECTION_TINT.identity}
                  hint={s.status === 'OPEN' && s.progress.of
                    ? `${s.progress.done.length} of ${s.progress.of} answered`
                    : undefined}>
                  What they told us
                </SectionHead>
                <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                  {[
                    // Only when the title is a NAME; otherwise the title is already the number.
                    ...((s.farmerName ?? s.name) ? [{ k: 'Mobile', v: s.mobile, kind: 'phone' as const }] : []),
                    { k: 'Name', v: s.name, kind: 'text' as const },
                    { k: 'Village', v: s.village, kind: 'place' as const },
                    { k: 'District', v: s.districtText ?? s.districtName, kind: 'place' as const },
                    { k: 'State', v: s.stateName, kind: 'place' as const },
                    { k: 'Pincode', v: s.pincode, kind: 'code' as const },
                  ].map(({ k, v, kind }) => (
                    <div key={k} className="min-w-0">
                      <dt className="type-caption text-subtle">{k}</dt>
                      <dd className="mt-0.5 min-w-0 wrap-break-word type-body-strong text-content">
                        {!v ? (
                          <span className="inline-flex items-center gap-1 rounded-control bg-amber-500/10 px-2 py-0.5 type-caption text-amber-700 dark:text-amber-400">
                            Not answered
                          </span>
                        ) : kind === 'phone' ? (
                          <a href={`tel:${v}`} className="inline-flex items-center gap-1 rounded-control bg-emerald-500/10 px-2 py-0.5 font-mono type-body-strong text-emerald-700 hover:underline dark:text-emerald-400">
                            <PhoneCall className="size-3" />{v}
                          </a>
                        ) : kind === 'code' ? (
                          <span className="inline-block rounded-control bg-sky-500/10 px-2 py-0.5 font-mono type-body-strong text-sky-700 dark:text-sky-400">{v}</span>
                        ) : kind === 'place' ? (
                          <span className="inline-flex items-center gap-1 rounded-control bg-violet-500/10 px-2 py-0.5 type-body-strong text-violet-700 dark:text-violet-400">
                            <MapPin className="size-3" />{v}
                          </span>
                        ) : v}
                      </dd>
                    </div>
                  ))}
                </dl>
                {s.status === 'OPEN' && s.progress.missing.length > 0 && (
                  <div className="mt-3 border-t border-line pt-2.5">
                    {s.progress.done.length ? (
                      <div className="flex flex-wrap items-center gap-1.5 type-caption text-muted">
                        Still to answer:
                        {s.progress.missing.map((m) => (
                          <span key={m} className="inline-block rounded bg-amber-100 px-2 py-0.5 type-caption text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">{m}</span>
                        ))}
                      </div>
                    ) : (
                      <div className="type-caption text-muted">They have not started the form. It opens for them on their next sign-in.</div>
                    )}
                  </div>
                )}
                {s.status === 'CLOSED' && (
                  <div className="mt-3 flex items-start gap-2 rounded-control bg-surface-2 px-2.5 py-2 type-caption text-muted">
                    <CircleSlash className="mt-px size-3.5 shrink-0 text-subtle" />
                    <span>Closed — {s.closedReasonLabel}{s.closedNote ? `: ${s.closedNote}` : ''}{s.closedByName ? ` (${s.closedByName})` : ''}</span>
                  </div>
                )}
                {s.status === 'COMPLETED' && (
                  <div className="mt-3 flex items-center gap-2 rounded-control bg-emerald-500/10 px-2.5 py-2 type-caption text-emerald-700 dark:text-emerald-400">
                    <CheckCircle2 className="size-3.5 shrink-0" />
                    <span>Now a farmer: <span className="type-body-strong">{s.farmerName}</span></span>
                  </div>
                )}
              </section>
              <section className="rounded-card border border-line bg-surface p-3.5">
                <SectionHead icon={<PhoneCall className="size-3.5" />} tint={SECTION_TINT.contact}
                  hint={(s.contacts ?? []).length ? `${s.contacts!.length}` : undefined}>
                  Calls
                </SectionHead>
                {(s.contacts ?? []).length === 0 ? (
                  // An empty list is a prompt here, not a gap: calling is the whole job of this screen.
                  <div className="flex items-center gap-2 type-body text-muted">
                    <PhoneOutgoing className="size-4 text-subtle" />Nobody has called yet.
                  </div>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {s.contacts!.map((c) => (
                      <li key={c.id} className="rounded-control border border-line bg-surface-2/40 p-2">
                        <div className="flex justify-between gap-2">
                          <span className="inline-flex items-center gap-1.5 type-body-strong text-content">
                            <PhoneCall className="size-3.5 text-emerald-600 dark:text-emerald-400" />{c.outcomeLabel}
                          </span>
                          <span className="whitespace-nowrap type-caption text-muted">{formatIstDate(c.createdAt)}, {formatIstTime(c.createdAt)}</span>
                        </div>
                        {c.note && <div className="mt-0.5 type-body text-content">{c.note}</div>}
                        {c.byName && (
                          <div className="mt-0.5 flex flex-wrap items-center gap-1 type-caption text-muted">
                            by {c.byName}{c.byRole && <RoleBadge role={c.byRole} />}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}
          {mode === 'call' && (
            <>
              <Field label="What happened?" required>
                <Select placeholder="Choose" value={outcome} onChange={setOutcome} options={opts?.outcomes ?? []} />
              </Field>
              <Field label="Note" hint="Optional"><Textarea rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
            </>
          )}
          {mode === 'close' && (
            <>
              <Field label="Why close it?" required>
                <Select placeholder="Choose a reason" value={reason} onChange={setReason} options={opts?.closeReasons ?? []} />
              </Field>
              <Field label="Note" required hint="A few words on what they said.">
                <Textarea rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
              </Field>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

/** The farmer's own form, filled in by staff on a call. Placement and the sales person follow the same rules. */
function CompleteForSignup({ signup, onCancel, onDone }: { signup: Signup; onCancel: () => void; onDone: () => void }) {
  const [f, setF] = useState({
    fullName: signup.name ?? '', stateCode: signup.stateCode ?? '', districtId: signup.districtId ?? '',
    districtText: signup.districtText ?? '', village: signup.village ?? '', pincode: signup.pincode ?? '',
  });
  const [typed, setTyped] = useState(!!signup.districtText && !signup.districtId);
  const [agreed, setAgreed] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { data: states = [] } = useQuery({ queryKey: ['onboarding-states'], queryFn: () => request<any[]>({ url: '/onboarding/states' }), staleTime: 3600_000 });
  const { data: districts = [], isFetching } = useQuery({
    queryKey: ['onboarding-districts', f.stateCode],
    queryFn: () => request<any[]>({ url: '/onboarding/districts', params: { stateCode: f.stateCode } }),
    enabled: !!f.stateCode, staleTime: 3600_000,
  });
  const typeIt = typed || (!!f.stateCode && !isFetching && districts.length === 0);

  const save = useConfirmedMutation(
    { title: `Register ${f.fullName.trim() || 'this farmer'}?`, content: 'They become a farmer, placed from these answers and given to that area\'s sales person to verify.' },
    {
      mutationFn: () => request<any>({
        url: `/farmer-signups/${signup.id}/complete`, method: 'POST',
        data: {
          fullName: f.fullName, stateCode: f.stateCode, village: f.village, pincode: f.pincode, consent: agreed,
          districtId: typeIt ? null : f.districtId || null, districtText: typeIt ? f.districtText : null,
        },
      }),
      onSuccess: (r: any) => {
        message.success(r?.representative ? `Registered — ${r.representative.name} will verify` : 'Registered — no sales person covers that area yet');
        onDone();
      },
      onError: (e: any) => {
        const err = e?.response?.data?.error;
        if (err?.details) setErrors(err.details);
        message.error(err?.message ?? 'Could not register');
      },
    },
  );

  return (
    <div className="flex flex-col gap-3">
      <Field label="Full name" required error={errors.name}><Input value={f.fullName} maxLength={120} onChange={(e) => setF({ ...f, fullName: e.target.value })} /></Field>
      <Field label="State" required error={errors.state}>
        <Select showSearch optionFilterProp="label" placeholder="Choose the state" value={f.stateCode || null}
          options={states.map((s: any) => ({ value: s.code, label: s.covered ? s.name : `${s.name} (no territory yet)` }))}
          onChange={(v) => { setTyped(false); setF({ ...f, stateCode: v, districtId: '', districtText: '' }); }} />
      </Field>
      <Field label="District" required error={errors.district}>
        {typeIt ? (
          <Input value={f.districtText} maxLength={120} placeholder="District" disabled={!f.stateCode} onChange={(e) => setF({ ...f, districtText: e.target.value, districtId: '' })} />
        ) : (
          <Select showSearch optionFilterProp="label" placeholder="Choose the district" value={f.districtId || null} disabled={!f.stateCode} loading={isFetching}
            options={[...districts.map((x: any) => ({ value: x.id, label: x.name })), { value: '__other__', label: 'Not in this list' }]}
            onChange={(v) => { if (v === '__other__') { setTyped(true); setF({ ...f, districtId: '' }); } else setF({ ...f, districtId: v, districtText: '' }); }} />
        )}
      </Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_140px]">
        <Field label="Village or town" required error={errors.village}><Input value={f.village} maxLength={120} onChange={(e) => setF({ ...f, village: e.target.value })} /></Field>
        <Field label="Pincode" required error={errors.pincode}><Input inputMode="numeric" maxLength={6} value={f.pincode} onChange={(e) => setF({ ...f, pincode: e.target.value.replace(/\D/g, '') })} /></Field>
      </div>
      <Checkbox checked={agreed} onCheckedChange={(c) => setAgreed(!!c)} label="The farmer agreed on the call that Green Bay may store these details and contact them." />
      {errors.consent && <p className="type-caption text-danger-600">{errors.consent}</p>}
      <div className="mt-2 flex justify-end gap-2">
        <Button onClick={onCancel}>Back</Button>
        <Button variant="primary" disabled={!agreed} loading={save.isPending} onClick={() => save.mutate()}>Register farmer</Button>
      </div>
    </div>
  );
}
